import React, { useMemo, useState, useEffect } from 'react';
import { Card, Checkbox, Button, Alert, Tag, Spin, Empty, message, Space } from 'antd';
import { ExportOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useMyCompanies } from '../hooks/useMyCompanies';
import { putCompanyHandoff } from '../services/companyBoot';
import { getWorkspaceLanding } from '../data/landing';

/**
 * Portal เลือกบริษัท — เปิดหน้างานของแต่ละบริษัทเป็นแท็บแยก
 *
 * ทำไมต้องแยกแท็บ ไม่ใช่สลับในแท็บเดียว:
 *   sessionStorage แยกกันคนละแท็บอยู่แล้ว แต่ละแท็บจึงถือ activeCompanyId
 *   ของตัวเองได้จริง ๆ ไม่ตีกัน — ถ้าสลับในแท็บเดียวจะเห็นได้ทีละบริษัท
 *
 * ข้อจำกัดที่เลี่ยงไม่ได้: เบราว์เซอร์ยอมให้ window.open ได้แค่แท็บเดียวต่อการคลิก
 * ที่เหลือถือเป็น popup ต้องให้ผู้ใช้กด "อนุญาต" ครั้งแรกครั้งเดียว
 * โค้ดจึงตรวจว่าแท็บไหนถูกบล็อกแล้วโชว์ลิงก์สำรองให้กดเอง
 */
const CompanyPortal = ({ onOpened, compact = false }) => {
  const { data: companies = [], isLoading } = useMyCompanies();
  const [selected, setSelected] = useState([]);
  const [blocked, setBlocked] = useState([]);

  const currentCompanyId = sessionStorage.getItem('activeCompanyId');
  const role = sessionStorage.getItem('role');
  const jobType = Number(sessionStorage.getItem('jobType'));

  // ค่าเริ่มต้น: ติ๊กทุกบริษัทที่ "ยังไม่ได้เปิดอยู่ในแท็บนี้"
  useEffect(() => {
    if (!companies.length) return;
    setSelected(
      companies
        .filter((c) => String(c.id) !== String(currentCompanyId))
        .map((c) => c.id),
    );
  }, [companies, currentCompanyId]);

  const landing = useMemo(
    () => getWorkspaceLanding({ role, jobType }),
    [role, jobType],
  );

  const buildUrl = (companyId) => `${landing}?company=${companyId}`;

  const openTabs = () => {
    const picked = companies.filter((c) => selected.includes(c.id));
    if (!picked.length) {
      message.warning('เลือกบริษัทที่ต้องการเปิดก่อน');
      return;
    }

    const failed = [];
    picked.forEach((company) => {
      // ฝากชื่อ/สีไว้ก่อนเปิด แท็บใหม่จะได้โชว์ได้ทันทีไม่ต้องรอ API
      putCompanyHandoff(company);
      const win = window.open(buildUrl(company.id), '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        failed.push(company);
      }
    });

    setBlocked(failed);
    if (!failed.length) {
      message.success(`เปิด ${picked.length} บริษัทแล้ว`);
      onOpened?.();
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-10"><Spin /></div>;
  }

  if (!companies.length) {
    return <Empty description="ไม่พบบริษัทที่คุณมีสิทธิ์เข้าถึง" />;
  }

  return (
    <div>
      {!compact && (
        <p className="text-gray-500 mb-4">
          เลือกบริษัทที่ต้องการทำงาน แต่ละบริษัทจะเปิดเป็นแท็บแยกกัน ทำงานพร้อมกันได้เลย
        </p>
      )}

      <Checkbox.Group
        value={selected}
        onChange={setSelected}
        style={{ display: 'block', width: '100%' }}
      >
        <div className="flex flex-col gap-2">
          {companies.map((company) => {
            const isCurrent = String(company.id) === String(currentCompanyId);
            return (
              <Card
                key={company.id}
                size="small"
                bodyStyle={{ padding: '10px 14px' }}
                className={isCurrent ? 'bg-gray-50' : ''}
              >
                <Checkbox value={company.id} style={{ width: '100%' }}>
                  <Space size="small" wrap>
                    {company.logoUrl && (
                      <img
                        src={company.logoUrl}
                        alt=""
                        style={{ width: 22, height: 22, objectFit: 'contain', verticalAlign: 'middle' }}
                      />
                    )}
                    <span
                      style={{ fontWeight: 600, color: company.themeColor || undefined }}
                    >
                      {company.companyName}
                    </span>
                    {Number(company.isPrimary) === 1 && <Tag color="blue">บริษัทหลัก</Tag>}
                    {isCurrent && <Tag>แท็บนี้เปิดอยู่</Tag>}
                  </Space>
                </Checkbox>
              </Card>
            );
          })}
        </div>
      </Checkbox.Group>

      <Button
        type="primary"
        size="large"
        block
        className="mt-4"
        icon={<ExportOutlined />}
        onClick={openTabs}
        disabled={!selected.length}
      >
        เปิด {selected.length || ''} บริษัทที่เลือก
      </Button>

      {blocked.length > 0 && (
        <Alert
          className="mt-4"
          type="warning"
          showIcon
          icon={<AppstoreOutlined />}
          message="เบราว์เซอร์บล็อกการเปิดแท็บบางอัน"
          description={
            <div>
              <p className="mb-2">
                กดอนุญาต popup ของเว็บนี้ (ไอคอนที่แถบ URL) แล้วกดเปิดใหม่อีกครั้ง
                หรือกดลิงก์ข้างล่างทีละอันก็ได้
              </p>
              <div className="flex flex-col gap-1">
                {blocked.map((company) => (
                  <a
                    key={company.id}
                    href={buildUrl(company.id)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => putCompanyHandoff(company)}
                  >
                    เปิด {company.companyName}
                  </a>
                ))}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default CompanyPortal;
